/**
 * Подготовка изображения стикера в браузере (Canvas API, без зависимостей):
 * даунскейл больших исходников → обрезка прозрачных полей → вписывание в
 * 512×512 → кодирование в WebP с подбором качества под серверный лимит 256 КБ.
 * Если браузер не умеет кодировать WebP (Safari) — fallback в PNG.
 */

const MAX_BYTES = 250 * 1024 // лимит сервера 256 КБ, небольшой запас
const MAX_DIMENSION = 512
/**
 * Крупные исходники (12МП фото) сначала уменьшаются до этой стороны:
 * попиксельный трим по полному разрешению блокировал main thread на секунды,
 * а на iOS Safari превышал лимит площади канваса (getImageData возвращал нули
 * → молчаливо «пустой» стикер).
 */
const PRE_TRIM_DIMENSION = 1024
const ALPHA_THRESHOLD = 8
const TRIM_PADDING = 2

export interface PreparedStickerImage {
  file: File
  width: number
  height: number
}

export async function prepareStickerImage(source: File): Promise<PreparedStickerImage> {
  const released: HTMLCanvasElement[] = []
  const track = (canvas: HTMLCanvasElement): HTMLCanvasElement => {
    released.push(canvas)
    return canvas
  }

  try {
    let canvas = track(await drawSource(source))
    if (Math.max(canvas.width, canvas.height) > PRE_TRIM_DIMENSION) {
      canvas = track(fitInto(canvas, PRE_TRIM_DIMENSION))
    }
    canvas = track(trimTransparent(canvas))
    canvas = track(fitInto(canvas, MAX_DIMENSION))

    let blob = await encodeWebpUnderLimit(canvas, track)
    if (blob === null) {
      blob = await encodePngUnderLimit(canvas, track)
    }
    if (blob === null) {
      throw new Error('sticker image encode failed')
    }

    const baseName = source.name.replace(/\.[^.]+$/, '') || 'sticker'
    const extension = blob.type === 'image/webp' ? '.webp' : '.png'

    return {
      file: new File([blob], baseName + extension, { type: blob.type }),
      width: canvas.width,
      height: canvas.height,
    }
  } finally {
    // iOS Safari лимитирует суммарную площадь живых канвасов — освобождаем.
    for (const canvas of released) {
      canvas.width = 0
      canvas.height = 0
    }
  }
}

/**
 * Исходник → канвас. Предпочтительно createImageBitmap с учётом EXIF-поворота
 * (фото с телефона иначе запекается боком); fallback — HTMLImageElement.
 */
async function drawSource(file: File): Promise<HTMLCanvasElement> {
  if (typeof createImageBitmap === 'function') {
    try {
      const bitmap = await createImageBitmap(file, { imageOrientation: 'from-image' })
      const canvas = createCanvas(bitmap.width, bitmap.height)
      context(canvas).drawImage(bitmap, 0, 0)
      bitmap.close()
      return canvas
    } catch {
      // Формат не поддержан createImageBitmap — пробуем через <img>.
    }
  }

  const image = await loadImage(file)
  const canvas = createCanvas(image.naturalWidth, image.naturalHeight)
  context(canvas).drawImage(image, 0, 0)
  return canvas
}

function loadImage(file: File): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file)
    const image = new Image()
    image.onload = () => {
      URL.revokeObjectURL(url)
      resolve(image)
    }
    image.onerror = () => {
      URL.revokeObjectURL(url)
      reject(new Error('sticker image load failed'))
    }
    image.src = url
  })
}

function createCanvas(width: number, height: number): HTMLCanvasElement {
  const canvas = document.createElement('canvas')
  canvas.width = Math.max(1, Math.round(width))
  canvas.height = Math.max(1, Math.round(height))
  return canvas
}

function context(canvas: HTMLCanvasElement): CanvasRenderingContext2D {
  const ctx = canvas.getContext('2d')
  if (!ctx) throw new Error('canvas 2d context unavailable')
  return ctx
}

/** Обрезает прозрачные поля по краям (bounding box непрозрачных пикселей + отступ). */
function trimTransparent(canvas: HTMLCanvasElement): HTMLCanvasElement {
  const { width, height } = canvas
  const data = context(canvas).getImageData(0, 0, width, height).data

  let minX = width
  let minY = height
  let maxX = -1
  let maxY = -1

  for (let y = 0; y < height; y++) {
    for (let x = 0; x < width; x++) {
      if (data[(y * width + x) * 4 + 3] > ALPHA_THRESHOLD) {
        if (x < minX) minX = x
        if (x > maxX) maxX = x
        if (y < minY) minY = y
        if (y > maxY) maxY = y
      }
    }
  }

  // Ни одного видимого пикселя: битый исходник или canvas-лимит платформы —
  // честная ошибка вместо молчаливой загрузки пустого стикера.
  if (maxX < 0) {
    throw new Error('sticker image is fully transparent')
  }

  minX = Math.max(0, minX - TRIM_PADDING)
  minY = Math.max(0, minY - TRIM_PADDING)
  maxX = Math.min(width - 1, maxX + TRIM_PADDING)
  maxY = Math.min(height - 1, maxY + TRIM_PADDING)

  const trimWidth = maxX - minX + 1
  const trimHeight = maxY - minY + 1
  if (trimWidth === width && trimHeight === height) return canvas

  const trimmed = createCanvas(trimWidth, trimHeight)
  context(trimmed).drawImage(canvas, minX, minY, trimWidth, trimHeight, 0, 0, trimWidth, trimHeight)
  return trimmed
}

/** Вписывает в квадрат maxDimension с сохранением пропорций (без апскейла). */
function fitInto(canvas: HTMLCanvasElement, maxDimension: number): HTMLCanvasElement {
  const scale = Math.min(maxDimension / canvas.width, maxDimension / canvas.height, 1)
  if (scale === 1) return canvas
  return scaleCanvas(canvas, scale)
}

function scaleCanvas(canvas: HTMLCanvasElement, scale: number): HTMLCanvasElement {
  const scaled = createCanvas(canvas.width * scale, canvas.height * scale)
  const ctx = context(scaled)
  ctx.imageSmoothingQuality = 'high'
  ctx.drawImage(canvas, 0, 0, scaled.width, scaled.height)
  return scaled
}

function toBlob(canvas: HTMLCanvasElement, type: string, quality?: number): Promise<Blob | null> {
  return new Promise((resolve) => canvas.toBlob(resolve, type, quality))
}

type CanvasTracker = (canvas: HTMLCanvasElement) => HTMLCanvasElement

/** WebP с подбором качества и, при необходимости, уменьшением размера; null — браузер не кодирует WebP. */
async function encodeWebpUnderLimit(
  canvas: HTMLCanvasElement,
  track: CanvasTracker
): Promise<Blob | null> {
  for (const scale of [1, 0.85, 0.7, 0.55]) {
    const current = scale === 1 ? canvas : track(scaleCanvas(canvas, scale))
    for (const quality of [0.9, 0.8, 0.7, 0.6, 0.5, 0.4]) {
      const blob = await toBlob(current, 'image/webp', quality)
      // Safari молча возвращает PNG вместо WebP — уходим в PNG-fallback.
      if (blob === null || blob.type !== 'image/webp') return null
      if (blob.size <= MAX_BYTES) return blob
    }
  }
  return null
}

/** PNG-fallback: качество не настраивается, влезаем уменьшением размера. */
async function encodePngUnderLimit(
  canvas: HTMLCanvasElement,
  track: CanvasTracker
): Promise<Blob | null> {
  for (const scale of [1, 0.85, 0.7, 0.55, 0.4, 0.3]) {
    const current = scale === 1 ? canvas : track(scaleCanvas(canvas, scale))
    const blob = await toBlob(current, 'image/png')
    if (blob !== null && blob.size <= MAX_BYTES) return blob
  }
  return null
}
